import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { formatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { BaseServices } from '../../../kuba.services';
import { BusinessSettingService } from '../../../businesses/services/businessSetting.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import {
  DeviationQrCaseType,
  DeviationQrCodeListItem
} from '../../models/deviationQrCodeDto';
import { DeviationServices } from '../../services/deviation.services';
import { PosterSize, printPoster } from './deviation-qr-poster';

/**
 * Minimal shape of the angularx-qrcode component instance we rely on.
 * A template ref on the <qrcode> element resolves to the component instance,
 * which exposes its rendered wrapper through `qrcElement`.
 */
interface QrCodeComponentRef {
  qrcElement: ElementRef<HTMLElement>;
}

/** How a row's code reads on the screen. */
type QrCodeStatus = 'valid' | 'expiring' | 'expired' | 'none';

/** A code that expires within this many days is shown as expiring, so it can be replaced in time. */
const EXPIRING_WITHIN_DAYS = 30;

/** One row of the dialog, with what the template needs worked out once. */
interface QrCodeRow {
  item: DeviationQrCodeListItem;
  /** Identifies the row: the department id, or 'business' for the business-wide code. */
  key: string;
  name: string;
  status: QrCodeStatus;
}

/**
 * The QR codes dialog of the deviation list: one business-wide code and one code per active
 * department, each with its status, expiry and reports of the last 30 days, plus which case types
 * the anonymous form offers.
 *
 * A department code links its reports to the department and notifies that department's
 * recipients; the business-wide code notifies the business-level recipients. Revoking one code
 * leaves every other code working.
 */
@Component({
  selector: 'app-deviation-qr-generator',
  templateUrl: './deviation-qr-generator.component.html',
  styleUrls: ['./deviation-qr-generator.component.scss'],
  standalone: false
})
export class DeviationQrGeneratorComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @ViewChild('qrCode') qrCode?: QrCodeComponentRef;

  rows: QrCodeRow[] = [];
  caseTypes: DeviationQrCaseType[] = [];
  isLoading = false;

  /** The row whose code is drawn, downloaded and printed. */
  selectedKey: string | null = null;
  /** The row whose create form is open. */
  creatingKey: string | null = null;
  /** The row whose revoke confirmation is open. */
  revokingKey: string | null = null;

  /** Case types whose visibility is being saved, so their checkbox cannot be pressed twice. */
  savingCaseTypeIds = new Set<number>();

  readonly minDate = new Date();
  expirationDate: Date | null = null;

  private readonly destroy$ = new Subject<void>();
  private businessLogo: string | null = null;

  constructor(
    private deviationService: DeviationServices,
    private notification: NotificationService,
    private translate: TranslateService,
    private businessSettings: BusinessSettingService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onHide(): void {
    this.visible = false;
    this.creatingKey = null;
    this.revokingKey = null;
    this.visibleChange.emit(false);
  }

  // ===== Loading =====

  private load(): void {
    this.isLoading = true;
    this.businessLogo = null;
    forkJoin({
      codes: this.deviationService.getDeviationQrCodes(),
      caseTypes: this.deviationService.getDeviationQrCaseTypes(),
      settings: this.businessSettings.getSettingsByBusinessId(BaseServices.BusinessId)
    })
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: ({ codes, caseTypes, settings }) => {
          this.rows = codes.map(item => this.toRow(item));
          this.caseTypes = caseTypes;
          this.businessLogo = settings?.BusinessLogo ?? null;

          // Keep the drawn code when it still exists, otherwise show the first row that has one
          if (!this.selectedRow?.item.QrCode) {
            this.selectedKey = this.rows.find(row => row.item.QrCode)?.key ?? null;
          }
        }
      });
  }

  private toRow(item: DeviationQrCodeListItem): QrCodeRow {
    return {
      item,
      key: item.BusinessDepartmentId === null ? 'business' : String(item.BusinessDepartmentId),
      name: item.DepartmentName ?? this.translate.instant('DEVIATION_QR_ADMIN.WHOLE_BUSINESS'),
      status: this.statusOf(item)
    };
  }

  /** ExpiresOn is the last day the code works, so a code is expired only from the day after. */
  private statusOf(item: DeviationQrCodeListItem): QrCodeStatus {
    const expiresOn = item.QrCode?.ExpiresOn;
    if (!item.QrCode) {
      return 'none';
    }
    if (!expiresOn) {
      return 'valid';
    }

    const today = formatDate(new Date(), 'yyyy-MM-dd', 'en-US');
    if (expiresOn < today) {
      return 'expired';
    }

    const soon = new Date();
    soon.setDate(soon.getDate() + EXPIRING_WITHIN_DAYS);
    return expiresOn <= formatDate(soon, 'yyyy-MM-dd', 'en-US') ? 'expiring' : 'valid';
  }

  get selectedRow(): QrCodeRow | undefined {
    return this.rows.find(row => row.key === this.selectedKey);
  }

  /** Whether the row's code can be scanned, printed and revoked. An expired code can only be replaced. */
  isLive(row: QrCodeRow): boolean {
    return row.status === 'valid' || row.status === 'expiring';
  }

  expiryText(row: QrCodeRow): string {
    if (!row.item.QrCode) {
      return '—';
    }
    const expiresOn = row.item.QrCode.ExpiresOn;
    return expiresOn ? formatDate(expiresOn, 'dd.MM.yyyy', 'en-US') : this.translate.instant('DEVIATION_QR_ADMIN.NEVER');
  }

  get noCaseTypeIsPublic(): boolean {
    return this.caseTypes.length > 0 && this.caseTypes.every(caseType => !caseType.IsPublic);
  }

  // ===== Creating and revoking =====

  select(row: QrCodeRow): void {
    if (this.isLive(row)) {
      this.selectedKey = row.key;
    }
  }

  openCreate(row: QrCodeRow): void {
    this.creatingKey = row.key;
    this.revokingKey = null;
    this.expirationDate = null;
  }

  cancelCreate(): void {
    this.creatingKey = null;
  }

  /** Expiry as a local yyyy-MM-dd, the backend's DateOnly; local avoids the off-by-one of a UTC conversion. */
  create(row: QrCodeRow): void {
    const expiresOn = this.expirationDate ? formatDate(this.expirationDate, 'yyyy-MM-dd', 'en-US') : null;

    this.isLoading = true;
    this.deviationService
      .createDeviationQrCode(row.item.BusinessDepartmentId, expiresOn)
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.creatingKey = null;
          this.selectedKey = row.key;
          this.notification.success('UI_NOTIFICATIONS.SAVED');
          this.load();
        }
      });
  }

  askRevoke(row: QrCodeRow): void {
    this.revokingKey = row.key;
    this.creatingKey = null;
  }

  cancelRevoke(): void {
    this.revokingKey = null;
  }

  revoke(row: QrCodeRow): void {
    this.isLoading = true;
    this.deviationService
      .revokeDeviationQrCode(row.item.BusinessDepartmentId)
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.revokingKey = null;
          if (this.selectedKey === row.key) {
            this.selectedKey = null;
          }
          this.notification.success('UI_NOTIFICATIONS.SAVED');
          this.load();
        }
      });
  }

  // ===== Case types =====

  toggleCaseType(caseType: DeviationQrCaseType, event: Event): void {
    const isPublic = (event.target as HTMLInputElement).checked;
    this.savingCaseTypeIds.add(caseType.Id);

    this.deviationService
      .setDeviationQrCaseTypeVisibility(caseType.Id, isPublic)
      .pipe(
        finalize(() => this.savingCaseTypeIds.delete(caseType.Id)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          caseType.IsPublic = isPublic;
          this.notification.success('UI_NOTIFICATIONS.SAVED');
        },
        error: () => {
          // The interceptor has shown the failure; put the checkbox back to what is saved
          (event.target as HTMLInputElement).checked = caseType.IsPublic;
        }
      });
  }

  // ===== Download and print =====

  /** The drawn code as a PNG data URL, or null before it has rendered. */
  private qrCodeDataUrl(): string | null {
    const element = this.qrCode?.qrcElement?.nativeElement;
    if (!element) {
      return null;
    }

    const canvas = element.querySelector('canvas');
    const image = element.querySelector('img');
    return canvas ? canvas.toDataURL('image/png') : (image?.src ?? null);
  }

  downloadPng(): void {
    const dataUrl = this.qrCodeDataUrl();
    const row = this.selectedRow;
    if (!dataUrl || !row) {
      return;
    }

    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${this.translate.instant('DEVIATION_QR_CODE_FILE_NAME')}_${row.name.replace(/[^\p{L}\p{N}]+/gu, '_')}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  printPoster(size: PosterSize): void {
    const dataUrl = this.qrCodeDataUrl();
    const row = this.selectedRow;
    if (!dataUrl || !row?.item.QrCode) {
      return;
    }

    const t = (key: string, params?: object) => this.translate.instant(`DEVIATION_QR_ADMIN.${key}`, params);
    const expiresOn = row.item.QrCode.ExpiresOn;

    const opened = printPoster({
      size,
      language: this.translate.currentLang() || 'no',
      businessName: BaseServices.BusinessName ?? '',
      businessLogo: this.businessLogo,
      poweredBy: t('POSTER_POWERED_BY'),
      departmentName: row.item.DepartmentName,
      qrCodeDataUrl: dataUrl,
      qrCodeUrl: row.item.QrCode.QrCodeUrl,
      heading: t('POSTER_HEADING'),
      lead: t('POSTER_LEAD'),
      steps: [t('POSTER_STEP_1'), t('POSTER_STEP_2'), t('POSTER_STEP_3')],
      notWhistleblowing: t('POSTER_NOT_WHISTLEBLOWING'),
      validUntil: expiresOn ? t('POSTER_VALID_UNTIL', { date: formatDate(expiresOn, 'dd.MM.yyyy', 'en-US') }) : null,
      emergency: t('POSTER_EMERGENCY')
    });

    if (!opened) {
      this.notification.add({ severity: 'warn', detail: t('POPUP_BLOCKED') });
    }
  }
}
